import {
  Alert,
  Box,
  Chip,
  Paper,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material';
import { PeopleAlt, Shield } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import apiClient from '../../services/apiClient';
import { searchGal } from '../../services/graphService';
import type { GalUser } from '../../services/graphService';
import type { UserRecord } from './adminTypes';
import { GalSearchInline } from './GalSearchInline';
import { PendingApprovalsBanner } from './PendingApprovalsBanner';
import { UserTable } from './UserTable';
import { usePermissions } from '../../hooks/usePermissions';

export const AdminUsersPage = () => {
  const theme = useTheme();
  const { instance } = useMsal();
  const { isAdmin, isSuperAdmin } = usePermissions();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // GAL inline search state
  const [galQuery, setGalQuery] = useState('');
  const [galResults, setGalResults] = useState<GalUser[]>([]);
  const [galLoading, setGalLoading] = useState(false);
  const [galRole, setGalRole] = useState('reader');
  const [addingEmail, setAddingEmail] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/users');
      setUsers(res.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const pendingUsers = useMemo(() => users.filter((u) => u.Status === 'pending'), [users]);
  const activeUsers = useMemo(() => users.filter((u) => u.Status !== 'pending'), [users]);
  const existingEmails = useMemo(() => new Set(users.map((u) => u.Email.toLowerCase())), [users]);

  // GAL search — fires on every keystroke with 150ms debounce
  const handleGalSearch = useCallback((query: string) => {
    setGalQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setGalResults([]); setGalLoading(false); return; }
    setGalLoading(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchGal(instance as any, query);
      setGalResults(results);
      setGalLoading(false);
    }, 150);
  }, [instance]);

  const handleAddFromGal = async (galUser: GalUser) => {
    const email = galUser.mail || galUser.userPrincipalName;
    setAddingEmail(email);
    try {
      const res = await apiClient.post('/users', {
        email,
        displayName: galUser.displayName,
        role: galRole,
        entraObjectId: galUser.id,
        // Job Class comes straight from the GAL (Entra jobTitle); Tier is derived from it server-side.
        jobClass: galUser.jobTitle ?? undefined,
      });
      setUsers((prev) => [...prev, res.data]);
      setSnackbar({ open: true, message: `${galUser.displayName} added as ${galRole}`, severity: 'success' });
      setGalQuery('');
      setGalResults([]);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSnackbar({ open: true, message: axiosErr.response?.data?.error || 'Failed to add user', severity: 'error' });
    } finally {
      setAddingEmail(null);
    }
  };

  const updateUser = async (key: string, data: Record<string, unknown>) => {
    try {
      // Merge from the server response so server-derived fields (e.g. Tier auto-derived
      // from Job Class, or a guard that rejected a role change) stay in sync with the row.
      const res = await apiClient.put(`/users/${key}`, data);
      const srv = (res.data ?? {}) as Partial<UserRecord>;
      setUsers((prev) => prev.map((u) => {
        if (u.AppUserKey !== key) return u;
        return {
          ...u,
          ...(srv.Role !== undefined ? { Role: srv.Role } : {}),
          ...(srv.Status !== undefined ? { Status: srv.Status } : {}),
          ...(srv.IsAssignable !== undefined ? { IsAssignable: srv.IsAssignable } : {}),
          ...(srv.CanAccessChat !== undefined ? { CanAccessChat: srv.CanAccessChat } : {}),
          ...(srv.JobClass !== undefined ? { JobClass: srv.JobClass } : {}),
          ...(srv.WorkGroup !== undefined ? { WorkGroup: srv.WorkGroup } : {}),
          ...(srv.Tier !== undefined ? { Tier: srv.Tier } : {}),
        };
      }));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSnackbar({ open: true, message: axiosErr.response?.data?.error || 'Failed to update user', severity: 'error' });
    }
  };

  const handleApprove = async (user: UserRecord, role = 'reader') => {
    try {
      await apiClient.put(`/users/${user.AppUserKey}`, { status: 'active', role });
      setUsers((prev) => prev.map((u) =>
        u.AppUserKey === user.AppUserKey ? { ...u, Status: 'active', Role: role } : u,
      ));
      setSnackbar({ open: true, message: `${user.DisplayName} approved as ${role}`, severity: 'success' });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSnackbar({ open: true, message: axiosErr.response?.data?.error || 'Failed to approve user', severity: 'error' });
    }
  };

  const handleDelete = async (user: UserRecord) => {
    try {
      await apiClient.delete(`/users/${user.AppUserKey}`);
      setUsers((prev) => prev.filter((u) => u.AppUserKey !== user.AppUserKey));
      setSnackbar({ open: true, message: `${user.DisplayName} removed`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove user', severity: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
        <Shield sx={{ fontSize: 28, color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage access, roles, and approvals
          </Typography>
        </Box>
      </Box>

      {/* Pending Approvals — admin only */}
      {isAdmin && !loading && pendingUsers.length > 0 && (
        <PendingApprovalsBanner users={pendingUsers} onApprove={handleApprove} onReject={handleDelete} />
      )}

      {/* Inline GAL search — admin only */}
      {!isAdmin && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
          <Typography variant="body2" color="text.secondary">
            You are viewing User Management in read-only mode. Only Lab Admin and Super Admin can modify users.
          </Typography>
        </Box>
      )}
      {isAdmin && <GalSearchInline
        query={galQuery}
        results={galResults}
        loading={galLoading}
        role={galRole}
        existingEmails={existingEmails}
        addingEmail={addingEmail}
        canAssignElevatedRoles={isSuperAdmin}
        onSearch={handleGalSearch}
        onRoleChange={setGalRole}
        onAdd={handleAddFromGal}
      />}

      {/* All Users Table */}
      <Paper elevation={0} sx={{
        border: `1px solid ${alpha(theme.border.color, 0.6)}`,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: `0 1px 4px ${alpha('#000', 0.03)}`,
      }}>
        <Box sx={{
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.border.color, 0.4)}`,
          backgroundColor: alpha(theme.table.headerBg, 0.4),
        }}>
          <PeopleAlt sx={{ fontSize: 18, color: alpha(theme.palette.text.primary, 0.5) }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
            All Users
          </Typography>
          <Chip label={activeUsers.length} size="small" sx={{
            height: 20, fontSize: '0.7rem', fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 2.5 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={52} sx={{ mb: 0.5, borderRadius: '8px' }} />
            ))}
          </Box>
        ) : (
          <UserTable
            users={activeUsers}
            canAssignElevatedRoles={isSuperAdmin}
            onRoleChange={isAdmin ? (key, role) => updateUser(key, { role }) : undefined}
            onToggleStatus={isAdmin ? (key, current) => updateUser(key, { status: current === 'active' ? 'disabled' : 'active' }) : undefined}
            onDelete={isAdmin ? handleDelete : undefined}
            onToggleAssignable={isAdmin ? (key, current) => updateUser(key, { isAssignable: !current }) : undefined}
            onToggleChatAccess={isAdmin ? (key, current) => updateUser(key, { canAccessChat: !current }) : undefined}
            onSchedulingProfileChange={isAdmin ? (key, field, value) => updateUser(key, { [field]: value }) : undefined}
          />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
